import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Check, ArrowLeft, X, ShieldCheck, Clock, Lock, Languages, Compass } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/i18n";
import Seo from "@/components/Seo";
import { truncate, touristTripJsonLd, breadcrumbJsonLd, faqJsonLd } from "@/lib/seo";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const asList = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v.filter(Boolean);
  if (typeof v === "object") { const fr = v.fr; return Array.isArray(fr) ? fr.filter(Boolean) : (fr ? [fr] : []); }
  if (typeof v === "string") return v.trim() ? [v.trim()] : [];
  return [];
};

function Bars({ value, max = 5, label }) {
  return (
    <div>
      <div className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373] mb-2">{label}</div>
      <div className="flex gap-1">
        {[...Array(max)].map((_, i) => (
          <div key={i} className={`h-2 flex-1 ${i < value ? "bg-[#BC5D2E]" : "bg-[#262626]"}`} />
        ))}
      </div>
    </div>
  );
}

function fmtDate(iso, lang) {
  return new Date(iso).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "2-digit", month: "long", year: "numeric" });
}

const COUNTRIES = [
  { name: "France", code: "+33" }, { name: "Belgique", code: "+32" }, { name: "Suisse", code: "+41" },
  { name: "Luxembourg", code: "+352" }, { name: "Canada", code: "+1" }, { name: "Royaume-Uni", code: "+44" },
  { name: "Espagne", code: "+34" }, { name: "Allemagne", code: "+49" }, { name: "Italie", code: "+39" },
  { name: "Portugal", code: "+351" }, { name: "Maroc", code: "+212" }, { name: "États-Unis", code: "+1" },
  { name: "Émirats arabes unis", code: "+971" }, { name: "Autre", code: "+" },
];

const fmtDMY = (d) => {
  if (!d) return "";
  const s = String(d).slice(0, 10);
  const [y, m, day] = s.split("-");
  return `${day}.${m}.${y}`;
};
const availInfo = (av, t) => {
  if (!av) return null;
  if (av.sold_out || av.left <= 0) return { label: t("avail_full"), tone: "text-[#8A281E]" };
  if (av.left <= 2) return { label: `${t("avail_last")} : ${av.left}`, tone: "text-[#E0A05B]" };
  return { label: `${t("available")} : ${av.left}`, tone: "text-[#6FBF8B]" };
};

function ReserveDialog({ exp, mobile = false }) {
  const { t, lang } = useI18n();
  const datesAv = exp.dates_availability || [];
  const avByDate = Object.fromEntries(datesAv.map((d) => [String(d.date).slice(0, 10), d]));
  const firstDate = datesAv[0]?.date || "";
  const emptyForm = { first_name: "", last_name: "", email: "", phone: "", country: "France", country_code: "+33", is_whatsapp: false, participants: 1, date: firstDate, message: "", promo_code: "", consent_cgv: false, consent_privacy: false, consent_recontact: false };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);
  const [promo, setPromo] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const selAv = avByDate[String(form.date).slice(0, 10)];
  const dateFull = selAv ? (selAv.sold_out || selAv.left <= 0) : false;
  const maxP = Math.max(1, Number(selAv ? selAv.left : exp.spots_left) || 1);
  const unit = exp.promo_price || exp.price || 0;
  const baseTotal = unit * Math.max(1, Number(form.participants) || 1);

  const applyPromo = async () => {
    if (!form.promo_code) return;
    try {
      const { data } = await api.post("/promo/validate", { code: form.promo_code, expedition_id: exp.slug, email: form.email, amount: baseTotal });
      if (data.valid) { setPromo(data); toast.success(`${t("toast_code_applied")} ${data.discount_label}`); }
      else { setPromo(null); toast.error(data.message || t("toast_code_invalid")); }
    } catch { toast.error(t("toast_code_err")); }
  };

  const setCountry = (e) => {
    const c = COUNTRIES.find((x) => x.name === e.target.value) || COUNTRIES[0];
    setForm({ ...form, country: c.name, country_code: c.code });
  };

  const sendRequest = async (e) => {
    e.preventDefault();
    if (!(form.consent_cgv && form.consent_privacy && form.consent_recontact)) {
      toast.error(t("toast_consents"));
      return;
    }
    setSending(true);
    try {
      await api.post("/reservations", { ...form, expedition_id: exp.slug, participants: Number(form.participants) });
      toast.success(t("toast_sent"));
      setForm(emptyForm);
      setOpen(false);
    } catch (err) { toast.error(err?.response?.data?.detail || t("toast_send_err")); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {mobile ? (
          <button data-testid="open-reserve-btn-mobile" className="bg-[#BC5D2E] text-white px-6 py-3 uppercase tracking-widest text-xs hover:bg-[#8A281E] transition-colors whitespace-nowrap shrink-0">
            {t("req_join")}
          </button>
        ) : (
          <button data-testid="open-reserve-btn" className="w-full bg-[#BC5D2E] text-white px-8 py-4 uppercase tracking-widest text-sm hover:bg-[#8A281E] transition-colors">
            {t("req_join")}
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-[#0F0F0F] border border-white/15 rounded-none text-[#F5F5F0] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-clash uppercase tracking-tight text-2xl">{t("req_title")}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#A3A3A3] leading-relaxed mt-1" data-testid="reserve-intro">{t("req_reassure1")}</p>
        <form onSubmit={sendRequest} className="space-y-4 mt-2" data-testid="reserve-form">
          <div>
            <label className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373]">{t("select_date")}</label>
            <select required value={form.date} onChange={set("date")} className="hs-input mt-1 bg-[#0F0F0F]" data-testid="reserve-date">
              <option value="" disabled>{t("choose_date")}</option>
              {datesAv.map((a) => (
                <option key={a.date} value={a.date} disabled={a.sold_out || a.left <= 0}>
                  {fmtDMY(a.date)}{a.end ? ` – ${fmtDMY(a.end)}` : ""}
                </option>
              ))}
            </select>
            {(() => { const info = availInfo(selAv, t); return info ? (
              <p className={`mt-2 font-mono-hs text-xs uppercase tracking-widest ${info.tone}`} data-testid="reserve-date-availability">{info.label}</p>
            ) : null; })()}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder={t("req_firstname")} value={form.first_name} onChange={set("first_name")} className="hs-input" data-testid="reserve-firstname" />
            <input required placeholder={t("req_lastname")} value={form.last_name} onChange={set("last_name")} className="hs-input" data-testid="reserve-lastname" />
          </div>
          <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className="hs-input" data-testid="reserve-email" />
          <div>
            <label className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373]">{t("req_country")}</label>
            <select value={form.country} onChange={setCountry} className="hs-input mt-1 bg-[#0F0F0F]" data-testid="reserve-country">
              {COUNTRIES.map((c) => <option key={c.name} value={c.name}>{c.name} ({c.code})</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <span className="hs-input !w-20 flex items-center justify-center text-[#A3A3A3]" data-testid="reserve-dialcode">{form.country_code}</span>
            <input required type="tel" placeholder={t("req_phone")} value={form.phone} onChange={set("phone")} className="hs-input flex-1" data-testid="reserve-phone" />
          </div>
          <label className="flex items-center gap-2 text-xs text-[#A3A3A3] cursor-pointer" data-testid="reserve-whatsapp-label">
            <input type="checkbox" checked={form.is_whatsapp} onChange={(e) => setForm({ ...form, is_whatsapp: e.target.checked })} className="accent-[#BC5D2E]" data-testid="reserve-whatsapp" />
            {t("req_whatsapp")}
          </label>
          <div>
            <label className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373]">{t("participants")} (max {maxP})</label>
            <input required type="number" min="1" max={maxP} value={form.participants} onChange={set("participants")} className="hs-input mt-1" data-testid="reserve-participants" />
          </div>
          <textarea required placeholder={t("req_motivation")} value={form.message} onChange={set("message")} className="hs-input resize-none" rows={3} data-testid="reserve-note" />
          <div>
            <label className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373]">{t("req_promo")}</label>
            <div className="flex gap-2 mt-1">
              <input value={form.promo_code} onChange={(e) => { setForm({ ...form, promo_code: e.target.value.toUpperCase() }); setPromo(null); }} placeholder="HS-XXXXXX" className="hs-input flex-1 uppercase" data-testid="reserve-promo" />
              <button type="button" onClick={applyPromo} className="border border-white/30 px-4 uppercase tracking-widest text-xs hover:border-white" data-testid="reserve-promo-apply">{t("req_apply")}</button>
            </div>
            {promo && (
              <div className="mt-2 flex items-center justify-between text-sm border border-[#BC5D2E]/40 bg-[#BC5D2E]/5 px-3 py-2" data-testid="reserve-promo-applied">
                <span className="text-[#BC5D2E]">{promo.discount_label} {t("req_applied")}</span>
                <span className="text-[#F5F5F0]"><span className="line-through text-[#737373] mr-2">{baseTotal.toLocaleString()} €</span>{promo.new_amount.toLocaleString()} €</span>
              </div>
            )}
          </div>
          <div className="space-y-2 border-t border-white/10 pt-3">
            <label className="flex items-start gap-2 text-xs text-[#D4D4D4] cursor-pointer">
              <input type="checkbox" checked={form.consent_cgv} onChange={(e) => setForm({ ...form, consent_cgv: e.target.checked })} className="accent-[#BC5D2E] mt-0.5" data-testid="consent-cgv" />
              <span>{t("consent_cgv_1")}<a href="/cgv" target="_blank" className="text-[#BC5D2E] underline">{t("consent_cgv_name")}</a>.</span>
            </label>
            <label className="flex items-start gap-2 text-xs text-[#D4D4D4] cursor-pointer">
              <input type="checkbox" checked={form.consent_privacy} onChange={(e) => setForm({ ...form, consent_privacy: e.target.checked })} className="accent-[#BC5D2E] mt-0.5" data-testid="consent-privacy" />
              <span>{t("consent_privacy_1")}<a href="/confidentialite" target="_blank" className="text-[#BC5D2E] underline">{t("consent_privacy_name")}</a>.</span>
            </label>
            <label className="flex items-start gap-2 text-xs text-[#D4D4D4] cursor-pointer">
              <input type="checkbox" checked={form.consent_recontact} onChange={(e) => setForm({ ...form, consent_recontact: e.target.checked })} className="accent-[#BC5D2E] mt-0.5" data-testid="consent-recontact" />
              <span>{t("consent_recontact_txt")}</span>
            </label>
          </div>
          <button type="submit" disabled={sending || dateFull} data-testid="reserve-request-btn" className="w-full bg-[#BC5D2E] px-6 py-4 uppercase tracking-widest text-sm hover:bg-[#8A281E] transition-colors disabled:opacity-50 text-white">
            {sending ? t("req_sending") : dateFull ? t("req_datefull") : t("req_submit")}
          </button>
          <div className="grid grid-cols-1 gap-2.5 border-t border-white/10 pt-4" data-testid="reserve-trust">
            {[[ShieldCheck, t("reassure_payment")], [Clock, t("reassure_response")], [Lock, t("reassure_data")]].map(([Icon, label], i) => (
              <div key={i} className="flex items-center gap-3 text-xs text-[#A3A3A3]">
                <Icon className="w-4 h-4 text-[#BC5D2E] shrink-0" strokeWidth={1.5} />
                <span className="tracking-wide">{label}</span>
              </div>
            ))}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ExpeditionDetail() {
  const { slug } = useParams();
  const { t, L, lang } = useI18n();
  const nav = useNavigate();
  const [exp, setExp] = useState(null);
  const [gi, setGi] = useState(0);

  useEffect(() => {
    api.get(`/expeditions/${slug}?lang=${lang}`).then(({ data }) => {
      if (data && data.redirect_to) { nav(`/expeditions/${data.redirect_to}`, { replace: true }); return; }
      setExp(data);
    }).catch(() => nav("/expeditions"));
  }, [slug, lang]); // eslint-disable-line

  useEffect(() => {
    const show = exp && exp.status !== "coming_soon" && !exp.sold_out;
    document.body.classList.toggle("has-mobile-cta", !!show);
    return () => document.body.classList.remove("has-mobile-cta");
  }, [exp]);

  if (!exp) return <div className="pt-40 text-center font-mono-hs text-[#737373]">// ...</div>;

  const comingSoon = exp.status === "coming_soon";
  const en = lang === "en" ? (exp.translations?.en || {}) : {};
  const TL = (field) => (Array.isArray(en[field]) ? en[field] : asList(exp[field]));
  const faqList = Array.isArray(en.faq) ? en.faq : (Array.isArray(exp.faq) ? exp.faq : []);
  const coordinators = Array.isArray(exp.coordinators) ? exp.coordinators : [];
  const cl = (c, field) => {
    const enc = lang === "en" ? (c.translations?.en || {}) : {};
    const v = enc[field] != null && (Array.isArray(enc[field]) ? enc[field].length : String(enc[field]).trim()) ? enc[field] : c[field];
    return v;
  };

  const seoTitle = `${L(exp.title)} — HORS SIGNAL`;
  const seoDesc = truncate(L(exp.tagline) || L(exp.description));
  const seoImage = exp.cover || (exp.gallery && exp.gallery[0]) || undefined;
  const seoPrice = exp.promo_price || exp.price || 0;
  const seoSoldOut = exp.status === "sold_out" || exp.status === "Sold Out" || exp.availability === "waitlist";
  const seoJsonLd = [
    touristTripJsonLd({
      slug: exp.slug,
      title: L(exp.title),
      description: truncate(L(exp.description) || L(exp.tagline), 300),
      image: seoImage,
      price: seoPrice,
      soldOut: seoSoldOut,
    }),
    breadcrumbJsonLd([
      { name: lang === "en" ? "Home" : "Accueil", path: "/" },
      { name: lang === "en" ? "Expeditions" : "Expéditions", path: "/expeditions" },
      { name: L(exp.title), path: `/expeditions/${exp.slug}` },
    ]),
    faqList.length ? faqJsonLd(faqList.map((f) => [f.q, f.a])) : null,
  ];

  return (
    <div className="pt-20">
      <Seo
        lang={lang}
        type="article"
        path={`/expeditions/${exp.slug}`}
        title={seoTitle}
        description={seoDesc}
        image={seoImage}
        jsonLd={seoJsonLd}
      />
      {/* HERO */}
      <section className="relative h-[70vh] min-h-[460px] flex items-end overflow-hidden">
        <img src={exp.gallery[gi]} alt={L(exp.title)} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-black/40 to-black/30" />
        <div className="relative mx-auto max-w-7xl px-6 sm:px-12 pb-12 w-full">
          <button onClick={() => nav("/expeditions")} className="flex items-center gap-2 text-[#A3A3A3] hover:text-white text-xs uppercase tracking-widest mb-6"><ArrowLeft size={14} /> {t("nav_expeditions")}</button>
          <span className="font-mono-hs text-[10px] text-[#737373] tracking-widest uppercase block mb-3">{t("fiche_expedition")}</span>
          {comingSoon && <span data-testid="detail-comingsoon" className="inline-block font-mono-hs text-xs uppercase tracking-[0.2em] bg-[#BC5D2E] text-white px-4 py-2 mb-3">{t("coming_soon_badge")}</span>}
          <span className="block font-mono-hs text-sm text-[#BC5D2E] tracking-widest uppercase">{L(exp.location)}</span>
          <h1 className="font-clash font-bold text-5xl sm:text-7xl uppercase tracking-tighter mt-3">{L(exp.title)}</h1>
          <p className="text-[#D4D4D4] text-lg mt-4 max-w-2xl">{L(exp.tagline)}</p>
        </div>
      </section>

      {/* gallery thumbs */}
      <div className="mx-auto max-w-7xl px-6 sm:px-12 -mt-8 relative z-10 flex gap-3">
        {exp.gallery.map((g, i) => (
          <button key={i} onClick={() => setGi(i)} data-testid={`gallery-thumb-${i}`} className={`w-20 h-14 overflow-hidden border ${gi === i ? "border-[#BC5D2E]" : "border-white/10"}`}>
            <img src={g} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>

      <div className="mx-auto max-w-7xl px-6 sm:px-12 py-16 grid lg:grid-cols-3 gap-12">
        {/* LEFT */}
        <div className="lg:col-span-2 space-y-12">
          <p className="text-[#D4D4D4] text-lg leading-relaxed">{L(exp.description)}</p>

          <div className="grid sm:grid-cols-2 gap-8">
            <Bars value={exp.intensity} label={t("intensity")} />
            <Bars value={exp.risk} label={t("risk")} />
          </div>

          {coordinators.length > 0 && (
            <div data-testid="detail-coordinators">
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-8 bg-[#BC5D2E]" />
                <h3 className="font-clash text-2xl uppercase tracking-tight">{t("coord_section_title")}</h3>
              </div>
              <div className="space-y-4">
                {coordinators.map((c) => {
                  const langs = cl(c, "languages") || [];
                  const specs = cl(c, "specialties") || [];
                  return (
                    <div key={c.id} data-testid={`coordinator-${c.id}`}
                         className="relative bg-gradient-to-br from-[#151515] to-[#101010] border border-white/10 overflow-hidden transition-colors hover:border-[#BC5D2E]/50">
                      <span className="absolute left-0 top-0 bottom-0 w-1 bg-[#BC5D2E]" />
                      <div className="p-6 pl-7 flex flex-col sm:flex-row gap-6">
                        <div className="shrink-0 mx-auto sm:mx-0">
                          {c.photo
                            ? <img src={c.photo} alt={c.name} loading="lazy" className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover border-2 border-[#BC5D2E]/40 shadow-[0_0_30px_rgba(188,93,46,0.15)]" />
                            : <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-white/5 flex items-center justify-center text-[#555]"><Compass size={30} /></div>}
                        </div>
                        <div className="min-w-0 flex-1 text-center sm:text-left">
                          <h4 className="font-clash text-xl sm:text-2xl tracking-tight text-[#F5F5F0] leading-tight">{c.name}</h4>
                          {cl(c, "role") ? <div className="font-mono-hs text-[11px] uppercase tracking-[0.22em] text-[#BC5D2E] mt-1">{cl(c, "role")}</div> : null}
                          <div className="flex flex-wrap gap-2 mt-3 justify-center sm:justify-start">
                            {c.guide_since_year ? (
                              <span className="inline-flex items-center gap-1.5 font-mono-hs text-[10px] uppercase tracking-widest bg-white/5 border border-white/10 px-2.5 py-1 text-[#D4D4D4]">
                                <Clock size={11} className="text-[#BC5D2E]" /> {t("coord_guide_since")} {c.guide_since_year}
                              </span>
                            ) : null}
                            {langs.map((l, i) => (
                              <span key={i} className="inline-flex items-center gap-1.5 font-mono-hs text-[10px] uppercase tracking-widest bg-white/5 border border-white/10 px-2.5 py-1 text-[#D4D4D4]">
                                <Languages size={11} className="text-[#BC5D2E]" /> {l}
                              </span>
                            ))}
                          </div>
                          {specs.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2 justify-center sm:justify-start">
                              {specs.map((s, i) => (
                                <span key={i} className="font-mono-hs text-[10px] uppercase tracking-widest text-[#e0a583] border border-[#BC5D2E]/30 px-2.5 py-1">{s}</span>
                              ))}
                            </div>
                          )}
                          {cl(c, "bio") ? <p className="text-[#A3A3A3] text-sm leading-relaxed mt-4">{cl(c, "bio")}</p> : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <h3 className="font-clash text-2xl uppercase tracking-tight mb-4">{t("included")}</h3>
            <ul className="grid sm:grid-cols-2 gap-3">
              {TL("included").map((it, i) => (
                <li key={i} className="flex items-center gap-3 text-[#D4D4D4]"><Check size={16} className="text-[#BC5D2E]" /> {it}</li>
              ))}
            </ul>
          </div>

          {TL("excluded").length > 0 && (
            <div data-testid="detail-excluded">
              <h3 className="font-clash text-2xl uppercase tracking-tight mb-4">{t("d_excluded")}</h3>
              <ul className="grid sm:grid-cols-2 gap-3">
                {TL("excluded").map((it, i) => (
                  <li key={i} className="flex items-center gap-3 text-[#A3A3A3]"><X size={15} className="text-[#8A281E]" /> {it}</li>
                ))}
              </ul>
            </div>
          )}

          {TL("program").length > 0 && (
            <div data-testid="detail-program">
              <h3 className="font-clash text-2xl uppercase tracking-tight mb-4">{t("d_program")}</h3>
              <ol className="space-y-3">
                {TL("program").map((it, i) => (
                  <li key={i} className="flex gap-4 text-[#D4D4D4]"><span className="font-clash text-[#BC5D2E] shrink-0">{t("d_day")}{i + 1}</span><span>{it}</span></li>
                ))}
              </ol>
            </div>
          )}

          {TL("gear").length > 0 && (
            <div data-testid="detail-gear">
              <h3 className="font-clash text-2xl uppercase tracking-tight mb-4">{t("d_gear")}</h3>
              <ul className="grid sm:grid-cols-2 gap-3">
                {TL("gear").map((it, i) => (
                  <li key={i} className="flex items-center gap-3 text-[#D4D4D4]"><Check size={16} className="text-[#BC5D2E]" /> {it}</li>
                ))}
              </ul>
            </div>
          )}

          {TL("physical").length > 0 && (
            <div data-testid="detail-physical">
              <h3 className="font-clash text-2xl uppercase tracking-tight mb-4">{t("d_physical")}</h3>
              <ul className="space-y-2">
                {TL("physical").map((it, i) => <li key={i} className="text-[#D4D4D4] flex gap-3"><span className="text-[#BC5D2E]">—</span>{it}</li>)}
              </ul>
            </div>
          )}

          {TL("safety").length > 0 && (
            <div data-testid="detail-safety">
              <h3 className="font-clash text-2xl uppercase tracking-tight mb-4">{t("d_safety")}</h3>
              <ul className="space-y-2">
                {TL("safety").map((it, i) => <li key={i} className="text-[#D4D4D4] flex gap-3"><span className="text-[#8A281E]">!</span>{it}</li>)}
              </ul>
            </div>
          )}

          {TL("important_info").length > 0 && (
            <div data-testid="detail-important" className="border-l-2 border-[#BC5D2E] pl-5">
              <h3 className="font-clash text-2xl uppercase tracking-tight mb-4">{t("d_important")}</h3>
              <ul className="space-y-2">
                {TL("important_info").map((it, i) => <li key={i} className="text-[#D4D4D4]">{it}</li>)}
              </ul>
            </div>
          )}

          {faqList.length > 0 && (
            <div data-testid="detail-faq">
              <h3 className="font-clash text-2xl uppercase tracking-tight mb-4">FAQ</h3>
              <div className="space-y-4">
                {faqList.map((f, i) => (
                  <div key={i} className="border-b border-white/10 pb-4">
                    <p className="font-medium text-[#F5F5F0]">{f.q}</p>
                    <p className="text-[#A3A3A3] text-sm mt-1">{f.a}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT sticky */}
        <div className="lg:col-span-1">
          <div className="sticky top-28 bg-[#141414] border border-white/10 p-8 space-y-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373]">{t("from")}</div>
              {exp.promo_price ? (
                <div data-testid="detail-promo">
                  <div className="font-clash text-4xl text-[#BC5D2E] leading-none whitespace-nowrap">{Number(exp.promo_price).toLocaleString("fr-FR")} €</div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="font-mono-hs text-sm text-[#737373] line-through whitespace-nowrap">{Number(exp.price).toLocaleString("fr-FR")} €</span>
                    <span className="font-mono-hs text-[9px] uppercase tracking-widest bg-[#BC5D2E] text-white px-2 py-0.5">Promo</span>
                  </div>
                </div>
              ) : (
                <div className="font-clash text-4xl text-[#F5F5F0] leading-none whitespace-nowrap">{Number(exp.price).toLocaleString("fr-FR")} €</div>
              )}
            </div>
            <div className="text-right shrink-0">
              <div className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373] whitespace-nowrap">{exp.duration_days} {t("days")}</div>
              {comingSoon ? (
                <div className="font-mono-hs text-xs text-[#BC5D2E] mt-1" data-testid="detail-comingsoon-places">{t("coming_soon_places")}</div>
              ) : exp.sold_out ? (
                <div className="font-mono-hs text-xs text-[#8A281E] mt-1" data-testid="detail-complet">{t("avail_full")}</div>
              ) : (
                <div className="font-mono-hs text-xs text-[#BC5D2E] mt-1 whitespace-nowrap" data-testid="detail-places">{exp.spots_left} {t("spots_left")}</div>
              )}
            </div>
          </div>
            {exp.next_departure && (
              <div className="border-t border-white/10 pt-4 flex items-center justify-between" data-testid="detail-departure">
                <span className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373]">{t("departure")}</span>
                <span className="font-mono-hs text-sm text-[#F5F5F0]">
                  {new Date(exp.next_departure).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "2-digit", month: "long", year: "numeric" })}
                </span>
              </div>
            )}
            <div className="border-t border-white/10 pt-6">
              {comingSoon ? (
                <button disabled data-testid="detail-comingsoon-btn" className="w-full bg-[#262626] text-[#737373] px-8 py-4 uppercase tracking-widest text-sm cursor-not-allowed">{t("preview_only")}</button>
              ) : exp.sold_out ? (
                <button disabled data-testid="detail-complet-btn" className="w-full bg-[#262626] text-[#737373] px-8 py-4 uppercase tracking-widest text-sm cursor-not-allowed">{t("avail_full")}</button>
              ) : (
                <ReserveDialog exp={exp} />
              )}
            </div>
            <p className="font-mono-hs text-[10px] text-[#737373] text-center uppercase tracking-widest">{comingSoon ? t("reservations_soon") : t("group_note")}</p>
          </div>
        </div>
      </div>

      {!comingSoon && !exp.sold_out && (
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0F0F0F]/95 backdrop-blur border-t border-white/10 px-4 py-3 flex items-center justify-between gap-4" data-testid="mobile-reserve-bar">
          <div className="min-w-0">
            <div className="font-mono-hs text-[9px] uppercase tracking-widest text-[#737373]">{t("from")}</div>
            <div className="font-clash text-xl text-[#BC5D2E] leading-none whitespace-nowrap">{Number(exp.promo_price || exp.price).toLocaleString("fr-FR")} €</div>
          </div>
          <ReserveDialog exp={exp} mobile />
        </div>
      )}
    </div>
  );
}
